import { ClosedOneWayCircle } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { VENUE_STATUS_TITLE, VENUE_STATUS_TOOLTIPS } from '../helpers';
import { VenueAvailabilityStatus } from '../types';

const Light = styled.div`
  border-radius: 50%;
  height: 12px;
  width: 12px;
`;

const GrayLight = styled(Light)`
  background-color: ${cssThemedValue({ light: colors.pepper32, dark: colors.salt72 })};
`;

const GreenLight = styled(Light)`
  background-color: ${colors.lime100};
`;

const RedLight = styled(Light)`
  background-color: ${colors.strawberry100};
`;

const InactiveIcon = styled(ClosedOneWayCircle).attrs({ width: 12, height: 12 })`
  fill: ${colors.strawberry100};
`;

const StatusLabelContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const PopoverContent = styled.div`
  max-width: 188px;
`;

const StatusLabel = ({ icon, status }: { icon: JSX.Element; status: VenueAvailabilityStatus }) => (
  <StatusLabelContainer>
    {icon}
    <LegacyPopover
      content={
        <PopoverContent>
          <T.Body3>
            <FormattedMessage id={VENUE_STATUS_TOOLTIPS[status]} />
          </T.Body3>
        </PopoverContent>
      }
    >
      <T.Body3>
        <FormattedMessage id={VENUE_STATUS_TITLE[status]} />
      </T.Body3>
    </LegacyPopover>
  </StatusLabelContainer>
);

type Props = {
  status: VenueAvailabilityStatus;
};

const VenueStatusColumn: FC<Props> = ({ status }) => {
  switch (status) {
    case VenueAvailabilityStatus.IPAD_NOT_ALIVE:
      return <StatusLabel status={VenueAvailabilityStatus.IPAD_NOT_ALIVE} icon={<RedLight />} />;
    case VenueAvailabilityStatus.OFFLINE:
      return <StatusLabel status={VenueAvailabilityStatus.OFFLINE} icon={<RedLight />} />;
    case VenueAvailabilityStatus.CLOSED:
      return <StatusLabel status={VenueAvailabilityStatus.CLOSED} icon={<GrayLight />} />;
    case VenueAvailabilityStatus.AVAILABLE:
      return <StatusLabel status={VenueAvailabilityStatus.AVAILABLE} icon={<GreenLight />} />;
    default:
      return <StatusLabel status={VenueAvailabilityStatus.INACTIVE} icon={<InactiveIcon />} />;
  }
};

export default VenueStatusColumn;
